import type {
	FieldPath,
	FieldValues,
	UseControllerProps,
} from 'react-hook-form'

import React from 'react'
import { addYears, format } from 'date-fns'
import { CircleHelpIcon } from 'lucide-react'
import { useFormContext } from 'react-hook-form'

import {
	DatePicker,
	DatePickerCalendar,
	DatePickerContent,
	DatePickerInput,
} from '@/shared/components/ui/date-picker'
import {
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/shared/components/ui/form'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { getDatesLocale } from '@/shared/i18n'
import { DateFormat, utcToZonedTime } from '@/shared/tools/dates'

export default function RHFDate<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
	name,
	label,
	hint,
	description,
	format: formatProp = 'EU',
	defaultValue,
	shouldUnregister,
}: UseControllerProps<TFieldValues, TName> & {
	format?: 'EU' | 'US'
	label?: string
	hint?: string
	description?: string
}) {
	const { control } = useFormContext()

	return (
		<FormField
			name={name}
			defaultValue={defaultValue}
			shouldUnregister={shouldUnregister}
			control={control}
			render={({ field, fieldState: { error } }) => (
				<FormItem>
					{!!label && (
						<FormLabel>
							{label}
							{hint && (
								<Tooltip>
									<TooltipTrigger asChild>
										<CircleHelpIcon className='size-3 text-muted-foreground' />
									</TooltipTrigger>
									<TooltipContent side='top'>{hint}</TooltipContent>
								</Tooltip>
							)}
						</FormLabel>
					)}

					<DatePicker
						mode='single'
						value={
							field.value ? utcToZonedTime(new Date(field.value), 'UTC') : null
						}
						onValueChange={(date: Date | null) => {
							date
								? field.onChange(format(date, DateFormat.system))
								: field.onChange(null)
						}}
					>
						<FormControl>
							<DatePickerInput format={formatProp} aria-invalid={!!error} />
						</FormControl>
						<DatePickerContent>
							<DatePickerCalendar
								locale={getDatesLocale()}
								endMonth={addYears(new Date(), 2)}
								captionLayout='dropdown'
							/>
						</DatePickerContent>
					</DatePicker>
					{!!description && <FormDescription>{description}</FormDescription>}
					<FormMessage />
				</FormItem>
			)}
		/>
	)
}
